import { DOC_TYPES } from 'qap/constants'
import { normalizeCommentText } from 'utilities/reportComment'
import type { CommentState, ReportUserReviewStatus } from 'types'
import type { ActionOptions } from './types'
import { GENERIC_TRANSLATION_ERROR } from './constants'

export const FETCH_REPORT = 'FETCH_REPORT'
export const REQUIRE_REPORT_TRANSLATION = 'REQUIRE_REPORT_TRANSLATION'
export const POLL_REPORT_TRANSLATIONS = 'POLL_REPORT_TRANSLATIONS'
export const SET_TRANSLATION_TARGET_LANGUAGE = 'SET_TRANSLATION_TARGET_LANGUAGE'
export const SET_REPORT_TRANSLATION = 'SET_REPORT_TRANSLATION'
export const SET_TRANSLATION_ERROR = 'SET_TRANSLATION_ERROR'
export const CLEAR_REPORT_DATA = 'CLEAR_REPORT_DATA'
export const APPLY_REPORT_COMMENT_FILTER = 'APPLY_COMMENT_FILTER'
export const SET_REPORT_COMMENT_STATE = 'REPORT_COMMENT_STATE'
export const CLEAR_REPORT_COMMENT_STATE = 'CLEAR_REPORT_COMMENT_STATE'
export const API_REPORTS_ATTACHMENTS_CREATE = 'API_REPORTS_ATTACHMENTS_CREATE'
export const API_REPORTS_ATTACHMENTS_UPDATE = 'API_REPORTS_ATTACHMENTS_UPDATE'
export const API_REPORTS_ATTACHMENTS_DESTROY = 'API_REPORTS_ATTACHMENTS_DESTROY'
export const API_REPORTS_COMMENTS_CREATE = 'API_REPORTS_COMMENTS_CREATE'
export const API_REPORTS_COMMENTS_UPDATE = 'API_REPORTS_COMMENTS_UPDATE'
export const API_REPORTS_COMMENTS_DESTROY = 'API_REPORTS_COMMENTS_DESTROY'
export const API_REPORTS_REVIEW_STATUSES_UPDATE = 'API_REPORTS_REVIEW_STATUSES_UPDATE'
export const API_REPORTS_REVIEWERS_UPDATE = 'API_REPORTS_REVIEWERS_UPDATE'
export const API_REPORTS_USER_REVIEWS_CREATE = 'API_REPORTS_USER_REVIEWS_CREATE'
export const API_REPORTS_FOLLOW_UP_LABELS_UPDATE = 'API_REPORTS_FOLLOW_UP_LABELS_UPDATE'
export const API_REPORTS_FOLLOW_UP_LABELS_INDEX = 'API_REPORTS_FOLLOW_UP_LABELS_INDEX'
export const FETCH_RELATED_REPORTS = 'FETCH_RELATED_REPORTS'
export const API_REPORTS_ACTIVITY_INDEX = 'API_REPORTS_ACTIVITY_INDEX'
export const SET_EDIT_MODE = 'SET_EDIT_MODE'
export const SHOW_COMMENT_UPDATE_ERROR = 'SHOW_COMMENT_UPDATE_ERROR'
export const SHOW_COMMENT_CREATE_ERROR = 'SHOW_COMMENT_CREATE_ERROR'

// Report CAPA actions
export const API_REPORTS_CAPAS_CREATE = 'API_REPORTS_CAPAS_CREATE'
export const API_REPORTS_CAPAS_UPDATE = 'API_REPORTS_CAPAS_UPDATE'
export const API_REPORTS_CAPAS_CLOSE = 'API_REPORTS_CAPAS_CLOSE'
export const API_REPORTS_CAPAS_REOPEN = 'API_REPORTS_CAPAS_REOPEN'
export const SET_REPORT_CAPA_DRAFT = 'SET_REPORT_CAPA_DRAFT'
export const CLEAR_REPORT_CAPA_DRAFT = 'CLEAR_REPORT_CAPA_DRAFT'

export const REPORT_ACTION_OPTIONS = { docType: DOC_TYPES.report } as const
export const CERTIFICATE_ACTION_OPTIONS = { docType: DOC_TYPES.certificate } as const

// @ts-expect-error - TS7006 - Parameter 'actionCreator' implicitly has an 'any' type.
const createOperation = (actionCreator, operationId: string) => {
  actionCreator.operationId = operationId
  return actionCreator
}

export const fetchReport = (reportId: number, options: ActionOptions = REPORT_ACTION_OPTIONS) =>
  ({ type: FETCH_REPORT, reportId, options })

export const requireTranslation = (languageCode: string) => ({
  type: REQUIRE_REPORT_TRANSLATION,
  payload: { languageCode },
})

export const pollTranslations = (reportId: string, languageCode: string) => ({
  type: POLL_REPORT_TRANSLATIONS,
  payload: {
    reportId,
    languageCode,
  },
})

export const setTranslationTargetLanguage = (languageCode: string) => ({
  type: SET_TRANSLATION_TARGET_LANGUAGE,
  payload: { languageCode },
})

export const setTranslation = ({
  languageCode,
  url,
}: {
  languageCode: string;
  url: string;
}) => ({
  type: SET_REPORT_TRANSLATION,
  payload: { languageCode, url },
})

export const setTranslationError = (reportId: string, languageCode: string, error: string = GENERIC_TRANSLATION_ERROR) => ({
  type: SET_TRANSLATION_ERROR,
  payload: { reportId, languageCode, error },
})

export const clearReportData = () =>
  ({ type: CLEAR_REPORT_DATA })

export const setReportCommentState = (commentState: CommentState) => {
  return {
    type: SET_REPORT_COMMENT_STATE,
    commentState
  }
}

export const updateReportFollowUpLabel = createOperation(
  (
    reportId: string,
    customFollowUpLabelId: string,
    labelNote: string,
    { inline = true }: { inline?: boolean } = {},
  ) => ({
    type: API_REPORTS_FOLLOW_UP_LABELS_UPDATE,
    reportId,
    customFollowUpLabelId,
    labelNote,
    inline,
  }),
  API_REPORTS_FOLLOW_UP_LABELS_UPDATE
)

export const createReportAttachment = createOperation(
  (reportId: string, attachment: any) => ({ type: API_REPORTS_ATTACHMENTS_CREATE, reportId, attachment }),
  API_REPORTS_ATTACHMENTS_CREATE
)

export const updateReportAttachment = createOperation(
  (reportId: string, attachment: any) => ({ type: API_REPORTS_ATTACHMENTS_UPDATE, reportId, attachment }),
  API_REPORTS_ATTACHMENTS_UPDATE
)

export const deleteReportAttachment = createOperation(
  (reportId: string, attachment: any) => ({ type: API_REPORTS_ATTACHMENTS_DESTROY, reportId, attachment }),
  API_REPORTS_ATTACHMENTS_DESTROY
)

export const createReportComment = createOperation(
  (reportId: string, comment: any) => ({ type: API_REPORTS_COMMENTS_CREATE, reportId, comment: normalizeCommentText(comment) }),
  API_REPORTS_COMMENTS_CREATE
)

export const updateReportComment = createOperation(
  (reportId: string, comment: any) => ({ type: API_REPORTS_COMMENTS_UPDATE, reportId, comment }),
  API_REPORTS_COMMENTS_UPDATE
)

export const deleteReportComment = createOperation(
  (reportId: string, comment: any) => ({ type: API_REPORTS_COMMENTS_DESTROY, reportId, comment }),
  API_REPORTS_COMMENTS_DESTROY
)

export const updateReportReviewers = createOperation(
  (reportId: string, addedIds: Array<string>, removedIds: Array<string>) => (
    { type: API_REPORTS_REVIEWERS_UPDATE, reportId, addedIds, removedIds }
  ),
  API_REPORTS_REVIEWERS_UPDATE
)

export const updateUserReviewStatus = createOperation(
  (reportId: string, status: ReportUserReviewStatus) => ({ type: API_REPORTS_USER_REVIEWS_CREATE, reportId, status }),
  API_REPORTS_USER_REVIEWS_CREATE
)

export const updateReportReviewStatus = createOperation(
  (
    reportId: string,
    reviewStatus: string,
    { inline = true }: { inline?: boolean } = {},
  ) => ({
    type: API_REPORTS_REVIEW_STATUSES_UPDATE,
    reportId,
    reviewStatus,
    inline,
  }),
  API_REPORTS_REVIEW_STATUSES_UPDATE
)

// Report CAPA action creators
export const createReportCAPA = createOperation(
  (reportId: string, capaData: any) => ({ type: API_REPORTS_CAPAS_CREATE, reportId, capaData }),
  API_REPORTS_CAPAS_CREATE
)

export const updateReportCAPA = createOperation(
  (reportId: string, capaData: any) => ({ type: API_REPORTS_CAPAS_UPDATE, reportId, capaData }),
  API_REPORTS_CAPAS_UPDATE
)

export const closeReportCAPA = createOperation(
  (reportId: string, capaData: any) => ({ type: API_REPORTS_CAPAS_CLOSE, reportId, capaData }),
  API_REPORTS_CAPAS_CLOSE
)

export const reopenReportCAPA = createOperation(
  (reportId: string) => ({
    type: API_REPORTS_CAPAS_REOPEN,
    reportId,
  }),
  API_REPORTS_CAPAS_REOPEN
)

export const setReportCAPADraft = (draftData: any) => ({
  type: SET_REPORT_CAPA_DRAFT,
  draftData,
})

export const clearReportCAPADraft = () => ({
  type: CLEAR_REPORT_CAPA_DRAFT,
})

export const fetchRelatedReports = () => ({ type: FETCH_RELATED_REPORTS })

export const fetchReportCustomFollowUpLabels = (reportId: string) => ({
  type: API_REPORTS_FOLLOW_UP_LABELS_INDEX,
  reportId,
})

export const fetchReportActivity = (newOffset = 0) =>
  ({ type: API_REPORTS_ACTIVITY_INDEX, newOffset })

export const setEditMode = (isEditing: boolean, commentId?: string | null) => ({ type: SET_EDIT_MODE, payload: { isEditing, commentId } })
export const setShowCommentUpdateError = (value: boolean) => ({
  type: SHOW_COMMENT_UPDATE_ERROR,
  payload: {value},
})
export const setShowCommentCreateError = (value: boolean) => ({
  type: SHOW_COMMENT_CREATE_ERROR,
  payload: {value},
})
